import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileText, Plus, Trash2, UserCheck, AlertTriangle, Pencil, Check, X } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { UserProfile } from "@shared/schema";

type FichaComUsuario = {
  id: string;
  userId: string;
  especialidade: string;
  documentoFiscal?: string | null;
  usuario?: UserProfile;
  criadoEm: string;
};

export default function FichaTecnica() {
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const isRT = profile?.roles?.includes("rt") ?? false;

  const [showForm, setShowForm] = useState(false);
  const [userId, setUserId] = useState("");
  const [especialidade, setEspecialidade] = useState("");
  const [documentoFiscal, setDocumentoFiscal] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editEspecialidade, setEditEspecialidade] = useState("");
  const [editDocumentoFiscal, setEditDocumentoFiscal] = useState("");

  const { data: fichas, isLoading: fichasLoading } = useQuery<FichaComUsuario[]>({
    queryKey: ["/api/ficha-tecnica"],
    queryFn: async () => {
      const res = await fetch("/api/ficha-tecnica", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar ficha técnica");
      return res.json();
    },
    enabled: !!session,
  });

  const { data: usuarios, isLoading: usuariosLoading } = useQuery<UserProfile[]>({
    queryKey: ["/api/usuarios", profile?.empresaId],
    queryFn: async () => {
      const params = profile?.empresaId ? `?empresaId=${profile.empresaId}` : "";
      const res = await fetch(`/api/usuarios${params}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao carregar usuários");
      return res.json();
    },
    enabled: !!session && !!profile?.empresaId && showForm,
  });

  // Filtra usuários com cargo RT ou Admin que ainda não estão na ficha
  const usuariosElegiveis = (usuarios ?? []).filter((u) => {
    const isRtOrAdmin = u.roles.some((r) => ["rt", "admin"].includes(r));
    const jaIncluido = fichas?.some((f) => f.userId === u.id);
    return isRtOrAdmin && !jaIncluido;
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/ficha-tecnica", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ userId, especialidade, documentoFiscal: documentoFiscal || undefined }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ficha-tecnica"] });
      toast({ title: "Membro adicionado à ficha técnica" });
      setShowForm(false);
      setUserId("");
      setEspecialidade("");
      setDocumentoFiscal("");
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/ficha-tecnica/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ especialidade: editEspecialidade, documentoFiscal: editDocumentoFiscal || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ficha-tecnica"] });
      toast({ title: "Ficha técnica atualizada" });
      setEditingId(null);
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/ficha-tecnica/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao remover");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ficha-tecnica"] });
      toast({ title: "Membro removido da ficha técnica" });
    },
    onError: () => {
      toast({ title: "Erro ao remover", variant: "destructive" });
    },
  });

  function startEdit(ficha: FichaComUsuario) {
    setEditingId(ficha.id);
    setEditEspecialidade(ficha.especialidade);
    setEditDocumentoFiscal(ficha.documentoFiscal ?? "");
  }

  const canAdd = isAdmin && (fichas?.length ?? 0) < 2;
  const temRT = fichas?.some((f) => f.usuario?.roles?.includes("rt"));
  const temAdmin = fichas?.some((f) => f.usuario?.roles?.includes("admin"));
  const fichaCompleta = temRT && temAdmin;

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-display text-slate-900">Ficha Técnica de Elaboração</h1>
        <p className="text-muted-foreground mt-1">
          Configuração do rodapé técnico que aparece nos laudos PDF. São necessários exatamente 2 registros: um RT e um Admin. O RT é adicionado automaticamente ao ser cadastrado.
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Status da ficha */}
        {!fichasLoading && (
          <div className={`flex items-center gap-3 p-4 rounded-xl border-2 ${fichaCompleta ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
            {fichaCompleta ? (
              <UserCheck className="w-5 h-5 text-green-600 shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            )}
            <div>
              <p className={`font-semibold text-sm ${fichaCompleta ? "text-green-700" : "text-amber-700"}`}>
                {fichaCompleta
                  ? "Ficha técnica completa — aparecerá corretamente nos laudos PDF"
                  : "Ficha técnica incompleta — configure um RT e um Admin para que o rodapé apareça nos laudos"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {(fichas?.length ?? 0)}/2 membros configurados
                {!temRT && " · falta um RT"}
                {!temAdmin && " · falta um Admin"}
              </p>
            </div>
          </div>
        )}

        {/* Lista de membros */}
        <div className="space-y-3">
          {fichasLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : fichas && fichas.length > 0 ? (
            fichas.map((ficha) => {
              const canEdit = isAdmin || (isRT && ficha.userId === profile?.id);
              const isEditing = editingId === ficha.id;

              return (
                <Card key={ficha.id} className="border-none shadow-md">
                  <CardContent className="p-4">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-800">{ficha.usuario?.nome ?? "—"}</p>
                          {ficha.usuario?.roles?.includes("rt") && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">RT</Badge>
                          )}
                          {ficha.usuario?.roles?.includes("admin") && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Especialidade *</Label>
                          <Input
                            value={editEspecialidade}
                            onChange={(e) => setEditEspecialidade(e.target.value)}
                            placeholder="Ex: Engenheiro Mecânico, Perito Judicial"
                            autoFocus
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Documento Fiscal (CREA, CFT, etc.)</Label>
                          <Input
                            value={editDocumentoFiscal}
                            onChange={(e) => setEditDocumentoFiscal(e.target.value)}
                            placeholder="Número do documento (opcional)"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingId(null)}
                            disabled={editMutation.isPending}
                          >
                            <X className="w-3.5 h-3.5 mr-1" />
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            disabled={!editEspecialidade.trim() || editMutation.isPending}
                            onClick={() => editMutation.mutate(ficha.id)}
                          >
                            {editMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 mr-1" />
                            )}
                            Salvar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                            <FileText className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-slate-800">{ficha.usuario?.nome ?? "—"}</p>
                              {ficha.usuario?.roles?.includes("rt") && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">RT</Badge>
                              )}
                              {ficha.usuario?.roles?.includes("admin") && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-0.5">{ficha.especialidade}</p>
                            {ficha.documentoFiscal && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Doc. Fiscal: {ficha.documentoFiscal}
                              </p>
                            )}
                            {ficha.usuario?.crea && (
                              <p className="text-xs text-muted-foreground">CREA: {ficha.usuario.crea}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-500 hover:text-slate-800"
                              onClick={() => startEdit(ficha)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => deleteMutation.mutate(ficha.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <div className="text-center py-10 text-muted-foreground border-2 border-dashed rounded-xl">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Nenhum membro configurado</p>
            </div>
          )}
        </div>

        {/* Formulário de adição */}
        {canAdd && !showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Membro
          </Button>
        )}

        {showForm && (
          <Card className="border-2 border-primary/20 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Novo Membro</CardTitle>
              <CardDescription>
                Selecione um usuário RT ou Admin da sua vistoriadora
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Usuário (RT ou Admin) *</Label>
                {usuariosLoading ? (
                  <div className="text-sm text-muted-foreground">Carregando usuários...</div>
                ) : usuariosElegiveis.length === 0 ? (
                  <div className="text-sm text-amber-600">
                    Não há usuários RT ou Admin disponíveis (ou todos já foram adicionados).
                  </div>
                ) : (
                  <Select value={userId} onValueChange={setUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o usuário" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuariosElegiveis.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.nome} — {u.roles.filter((r) => ["rt", "admin"].includes(r)).join(", ").toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Especialidade *</Label>
                <Input
                  value={especialidade}
                  onChange={(e) => setEspecialidade(e.target.value)}
                  placeholder="Ex: Engenheiro Mecânico, Perito Judicial"
                />
              </div>

              <div className="space-y-2">
                <Label>Documento Fiscal (CREA, CFT, etc.)</Label>
                <Input
                  value={documentoFiscal}
                  onChange={(e) => setDocumentoFiscal(e.target.value)}
                  placeholder="Número do documento (opcional)"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button
                  disabled={!userId || !especialidade || addMutation.isPending}
                  onClick={() => addMutation.mutate()}
                >
                  {addMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isAdmin && !isRT && (
          <p className="text-xs text-muted-foreground">
            Apenas administradores podem configurar a ficha técnica de elaboração.
          </p>
        )}
      </div>
    </Shell>
  );
}
